// Peace Love Wellness — Matching Taxonomy (single source of truth)
//
// Machine-loadable version of PLW_Matching_Taxonomy_Data.md. This is the ONE
// definition that drives: the Get Matched prompts, the Find Your Therapist
// fit-facets, and the matching engine — so all of them derive from one place
// and can't drift. UI labels are generated FROM this file; never hand-copied.
//
// Pairs with:
//   - src/data/clinicians.js   (the 6 seed records, keyed by FACETS[].id)
//   - src/utils/matching.js    (ranking — may read public + publicClinical + internal)
//   - src/utils/disclosure.js  (render gate — the only source of display strings)

// ── 1. Disclosure levels ──────────────────────────────────────────────────
// public         → personal disclosure; used for matching; shown ("named directly")
// public-clinical→ resonance, no personal claim; used + shown ("works well with")
// internal       → used for matching silently; NEVER shown
// skip           → not consented / not collected; not used, not shown
export const DISCLOSURE_LEVELS = {
  public:         { id: 'public',          usedForMatching: true,  shownPublic: true,  display: 'personal' },
  publicClinical: { id: 'public-clinical', usedForMatching: true,  shownPublic: true,  display: 'resonance' },
  internal:       { id: 'internal',        usedForMatching: true,  shownPublic: false, display: 'none' },
  skip:           { id: 'skip',            usedForMatching: false, shownPublic: false, display: 'none' },
};

// Levels (by clinician-record value) that the matching engine MAY score on.
export const MATCHABLE_LEVELS = ['public', 'publicClinical', 'internal'];
// Levels that may ever be rendered to a client.
export const DISPLAYABLE_LEVELS = ['public', 'publicClinical'];

// `FILL:` markers are unfinished gaps from the seed data. Unknown ≠ false:
// a FILL value must NEVER render and must NEVER be used as a hard filter.
// Shared by matching.js (scope/insurance) and disclosure.js (display).
export function isFill(value) {
  if (value == null) return true;
  if (typeof value !== 'string') return false;
  return /^\s*fill\b/i.test(value) || value.trim() === '';
}

// Returns the strings in a list that are real (drops FILL/empty placeholders).
export function withoutFill(list) {
  return (Array.isArray(list) ? list : []).filter((v) => !isFill(v));
}

// ── 2. Axis A — identity & lived-experience facets (canonical 10) ──────────
// `getMatchedPrompt` is the client-facing Step 2 wording ("understands my…").
// `matchType: "soft"` — identity facets are ALWAYS soft-scored, never hard filters.
export const FACETS = [
  {
    id: 'race', axis: 'A', label: 'Race, ethnicity & culture',
    formField: 'Race, ethnicity, culture, ancestry',
    getMatchedPrompt: 'Understands my race, ethnicity, or cultural background',
    pageGroupings: ['identity', 'culture-family'], matchType: 'soft',
  },
  {
    id: 'language', axis: 'A', label: 'Language, immigration & bicultural experience',
    formField: 'Language, immigration, bicultural/biracial experience',
    getMatchedPrompt: 'Gets immigrant-family, bicultural, or between-worlds experience',
    pageGroupings: ['culture-family'], matchType: 'soft',
    // SOFT resonance only. "Therapy must be in Spanish" is a SEPARATE hard filter
    // (PRACTICAL_CONSTRAINTS.therapyLanguage). Keep the two distinct so bicultural
    // resonance never accidentally hard-excludes a clinician.
  },
  {
    id: 'gender', axis: 'A', label: 'Gender & gender-expansive experience',
    formField: 'Gender identity / gender-expansive experience',
    getMatchedPrompt: 'Affirming of my gender / gets gender-expansive experience',
    pageGroupings: ['identity'], matchType: 'soft',
  },
  {
    id: 'sexuality', axis: 'A', label: 'Sexuality & queer identity',
    formField: 'Sexuality, queer identity, chosen family',
    getMatchedPrompt: 'Affirming and knowledgeable about queer experience and chosen family',
    pageGroupings: ['identity'], matchType: 'soft',
  },
  {
    id: 'relationshipDiversity', axis: 'A', label: 'Relationship diversity (ENM/poly/kink/intimacy)',
    formField: 'Relationship structure, ENM, poly, kink, intimacy',
    getMatchedPrompt: 'Knowledgeable about non-monogamy, poly, kink, or intimacy',
    pageGroupings: ['relationships'], matchType: 'soft',
  },
  {
    id: 'neurodivergence', axis: 'A', label: 'Neurodivergence',
    formField: 'Neurodivergence, ADHD, autism, masking',
    getMatchedPrompt: 'Gets neurodivergence — ADHD, autism, masking',
    pageGroupings: ['body-embodiment'], matchType: 'soft',
  },
  {
    id: 'disabilityBody', axis: 'A', label: 'Disability, chronic illness & the body',
    formField: 'Disability, chronic illness, pain, body relationship',
    getMatchedPrompt: 'Understands chronic illness, disability, or my relationship with my body',
    pageGroupings: ['body-embodiment'], matchType: 'soft',
  },
  {
    id: 'religionMeaning', axis: 'A', label: 'Religion, spirituality & meaning',
    formField: 'Religion, spirituality, religious trauma, meaning',
    getMatchedPrompt: 'Holds religion, spirituality, or religious harm with care',
    pageGroupings: ['identity'], matchType: 'soft',
  },
  {
    id: 'familySystems', axis: 'A', label: 'Family systems & intergenerational patterns',
    formField: 'Family systems, intergenerational patterns, upbringing',
    getMatchedPrompt: 'Understands family roles and inherited patterns',
    pageGroupings: ['culture-family', 'trauma-patterns'], matchType: 'soft',
  },
  {
    id: 'classWork', axis: 'A', label: 'Class, work & achievement',
    formField: 'Class, education, work, achievement pressure',
    getMatchedPrompt: 'Gets class, work, or achievement pressure',
    pageGroupings: ['identity'], matchType: 'soft',
  },
];

export const FACET_BY_ID = Object.fromEntries(FACETS.map((f) => [f.id, f]));

// Neutral, experience-first resonance phrases used in reason text — framed as
// "therapy that understands [phrase]", NEVER as "this therapist IS X like you".
// Used when a clinician hasn't given a specific consented `clinical` resonance
// line for a matched facet, so we describe the area of fit without naming
// anyone's personal identity.
export const FACET_REASON_RESONANCE = {
  race: 'your race, ethnicity, and cultural background',
  language: 'bicultural, immigrant-family, and between-worlds experience',
  gender: 'gender and gender-expansive experience',
  sexuality: 'queer experience and chosen family',
  relationshipDiversity: 'non-monogamy, kink, and intimacy',
  neurodivergence: 'neurodivergence — ADHD, autism, and masking',
  disabilityBody: 'chronic illness, disability, and your relationship with your body',
  religionMeaning: 'religion, spirituality, and meaning',
  familySystems: 'family roles and inherited patterns',
  classWork: 'class, work, and achievement pressure',
};

// ── 3. Axis B — fit dimensions ─────────────────────────────────────────────
export const FIT_DIMENSIONS = [
  {
    id: 'concern', axis: 'B', label: 'Presenting concern',
    source: 'clinical resonance + scope', matchType: 'soft',
    // Drives the Step 1 checklist + "Good fit for" chips. Client-friendly labels live below.
    options: [
      'anxiety', 'panic', 'ocd', 'depression', 'trauma', 'grief', 'adhd', 'anger-regulation',
      'relationship-patterns', 'relationship-anxiety', 'family-roles', 'identity', 'burnout',
      'perfectionism', 'self-esteem', 'life-transitions', 'perinatal-fertility', 'health-anxiety',
      'religious-trauma', 'mens-issues', 'substance-use', 'serious-mental-illness',
    ],
  },
  {
    id: 'style', axis: 'B', label: 'Therapy style & embodied presence',
    source: 'embodiment page', matchType: 'soft-preference',
    // Client-facing style options, described in the clinicians' own words from
    // their "What therapy feels like" / approach sections. Clinician records
    // carry these ids in `style`; profiles keep their original adjectives.
    options: [
      'warm-supportive', 'gentle-paced', 'honest-challenging', 'practical-tools',
      'deep-exploratory', 'body-aware', 'steady-grounded',
    ],
  },
];

export const CONCERN_DIMENSION = FIT_DIMENSIONS.find((d) => d.id === 'concern');
export const STYLE_DIMENSION = FIT_DIMENSIONS.find((d) => d.id === 'style');

// Client-facing labels for Step 1 concern checklist (generated → never hand-copy in UI).
export const CONCERN_LABELS = {
  'anxiety': 'Anxiety',
  'panic': 'Panic',
  'depression': 'Depression',
  'trauma': 'Trauma & PTSD',
  'grief': 'Grief & loss',
  'adhd': 'ADHD & focus',
  'anger-regulation': 'Anger & emotional regulation',
  'relationship-patterns': 'Relationship patterns',
  'family-roles': 'Family roles & dynamics',
  'identity': 'Identity & self-trust',
  'burnout': 'Burnout & exhaustion',
  'self-esteem': 'Self-esteem',
  'life-transitions': 'Life transitions',
  'perinatal-fertility': 'Perinatal, fertility & postpartum',
  'ocd': 'OCD & intrusive thoughts',
  'relationship-anxiety': 'Relationship anxiety',
  'perfectionism': 'Perfectionism & high-achiever stress',
  'health-anxiety': 'Health anxiety & chronic illness',
  'religious-trauma': 'Religious trauma',
  'mens-issues': "Men's mental health",
  'substance-use': 'Substance use & recovery',
  'serious-mental-illness': 'Serious mental illness or psychosis',
};

// Concerns that need a conversation before a match: they still rank the right
// clinician, but Get Matched leads with "talk to us" and Find Your Therapist
// offers no public filter for them.
export const TALK_TO_US_CONCERNS = ['substance-use', 'serious-mental-illness'];

export const STYLE_LABELS = {
  'warm-supportive': 'Warm and supportive',
  'gentle-paced': 'Gentle, paced to my capacity',
  'honest-challenging': 'Honest and gently challenging',
  'practical-tools': 'Practical tools and skills',
  'deep-exploratory': 'Deep and exploratory',
  'body-aware': 'Body-aware and embodied',
  'steady-grounded': 'Steady and grounded',
};

export function styleLabel(id) {
  return STYLE_LABELS[id] || id;
}

// Treatment approaches. Clinician records map an approach id to THEIR exact
// wording (e.g. "EMDR-trained", never "EMDR-certified"), so filters can be
// shared while each profile states its precise training level.
export const APPROACHES = [
  { id: 'emdr', label: 'EMDR' },
  { id: 'dbt', label: 'DBT skills' },
  { id: 'ifs', label: 'IFS-informed' },
  { id: 'cbt', label: 'CBT' },
  { id: 'somatic', label: 'Somatic & body-based' },
  { id: 'creative-arts', label: 'Creative arts therapy' },
  { id: 'mindfulness', label: 'Mindfulness-based' },
  { id: 'person-centered', label: 'Person-centered' },
  { id: 'attachment', label: 'Attachment-focused' },
  { id: 'strengths', label: 'Strengths-based' },
  { id: 'integrative', label: 'Integrative' },
];

export const APPROACH_BY_ID = Object.fromEntries(APPROACHES.map((a) => [a.id, a]));

// Times of day a client can meet. Clinician records list the windows they offer.
export const SCHEDULE_WINDOWS = [
  { id: 'early-morning', label: 'Early mornings (before 9 am)' },
  { id: 'morning', label: 'Mornings' },
  { id: 'afternoon', label: 'Afternoons' },
  { id: 'early-evening', label: 'Early evenings (5–6 pm)' },
  { id: 'evening', label: 'Evenings (6 pm and later)' },
];

export const SCHEDULE_BY_ID = Object.fromEntries(SCHEDULE_WINDOWS.map((w) => [w.id, w]));

// Self-pay budget per session (Get Matched, shown when not using insurance).
// A clinician fits when their sliding-scale floor for the client's format is
// at or below the budget.
export const BUDGET_OPTIONS = [
  { value: '', label: 'Not sure' },
  { value: '100', label: 'Up to $100' },
  { value: '150', label: 'Up to $150' },
  { value: '200', label: 'Up to $200' },
  { value: 'any', label: 'More than $200' },
];

export function concernLabel(id) {
  return CONCERN_LABELS[id] || id.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── 4. Axis C — practical constraints (hard filters / facts) ────────────────
export const PRACTICAL_CONSTRAINTS = [
  { id: 'insurance',       axis: 'C', behavior: 'hardFilter',       source: 'basics' },
  { id: 'availability',    axis: 'C', behavior: 'hardFilter+sort',  values: ['accepting', 'limited', 'waitlist'] },
  { id: 'format',          axis: 'C', behavior: 'hardFilter',       values: ['individual', 'couples'] },
  { id: 'ageRange',        axis: 'C', behavior: 'hardFilter',       values: ['adults-18plus'] }, // adults only (18+), by design
  { id: 'schedule',        axis: 'C', behavior: 'hardFilter',       values: ['early-morning', 'morning', 'afternoon', 'early-evening', 'evening'] },
  { id: 'budget',          axis: 'C', behavior: 'hardFilter',       note: 'Self-pay only: sliding-scale floor for the format must be within budget.' },
  { id: 'licensure',       axis: 'C', behavior: 'preferenceFilter', values: ['licensed', 'supervised'] },
  {
    id: 'therapyLanguage', axis: 'C', behavior: 'hardFilter', source: 'clinician.languages',
    note: 'Hard filter ONLY when the client states therapy MUST be in a specific language ' +
      '(e.g., Spanish → Michelle). Distinct from the soft "language" lived-experience facet.',
  },
];

// ── 5. Page fit-facets (8) — DERIVED groupings; do not hand-maintain ────────
// These are the "Finding the Right Match for You" cards on the Find Your Therapist
// page. They reference FACETS ids so the page list and Get Matched stay in sync.
export const PAGE_FIT_FACETS = [
  { id: 'identity',        label: 'Identity & lived experience',          facets: ['race', 'gender', 'sexuality', 'religionMeaning', 'classWork'] },
  { id: 'culture-family',  label: 'Culture, family & belonging',          facets: ['race', 'language', 'familySystems'] },
  { id: 'body-embodiment', label: 'Body, neurodivergence & embodiment',   facets: ['neurodivergence', 'disabilityBody'], dimensions: ['style'] },
  { id: 'relationships',   label: 'Relationships, intimacy & attachment', facets: ['relationshipDiversity'], dimensions: ['concern'] },
  { id: 'trauma-patterns', label: 'Trauma, grief & emotional patterns',   facets: ['familySystems'], dimensions: ['concern'] },
  { id: 'style-presence',  label: 'Therapy style & presence',             dimensions: ['style'] },
  { id: 'insurance-avail', label: 'Insurance & availability',             practical: ['insurance', 'availability'] },
  { id: 'supervised-lic',  label: 'Supervised or fully licensed care',    practical: ['licensure'] },
];

// Scoring weights (tunable). Facet weight is multiplied by the priority weight.
export const WEIGHTS = {
  facetBase: 3,        // a selected facet the clinician can match
  priorityBoost: 2,    // extra multiplier when the client marked this facet "matters most"
  concern: 2,          // each matched presenting concern
  style: 1,            // each matched style preference
  approach: 2,         // each matched treatment-approach preference
  availability: 1,     // small boost for currently-accepting clinicians
};
